import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row, Divider } from 'antd';
import cn from 'classnames';
import { subject } from '@casl/ability';
import type { UploadFile } from 'antd/es/upload/interface';
import { PopupAssignToMe, PopupAttachFile, PopupReassignResponsibleRecruiter } from '_molecules/index';
import { Button, BUTTON_TYPES, File, User, AtsButtonMenu, notification, NOTIFICATION_TYPES } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { Status } from '_atoms/Status';
import { ACTIONS, Can, UI, useAbility } from 'permission';
import { attachFilesToCandidate, getCandidateStepAssignment, reassignResponsibleRecruiter } from 'actions';
import { uploadDocs } from '_templates/CandidateCreation/utils';
import { getUser } from 'store/selectors/authorizeSelectors';
import { dateFormatter } from 'helpers/dateFormatter';
import { candidateDocumentToFileItem } from 'helpers/candidateDocument';
import { useAppDispatch } from 'store';
import type { CandidateData, CandidateDocument } from 'store/reducers/candidateReducer';
import type { JobApprover, JobWorkflowStep } from 'store/reducers/jobReducer';
import { ReactComponent as ChainLink } from 'dist/icons/socialNetworks/Link.svg';
import { ReactComponent as Location } from 'dist/icons/location.svg';
import { ReactComponent as Clock } from 'dist/icons/clock.svg';
import { ReactComponent as Vector } from 'dist/icons/Vector1.svg';
import { ReactComponent as IconAttach } from 'dist/icons/attach.svg';
import { getStatusType } from './utils';
import './style.scss';
import { JobCandidateSummary } from './JobCandidateSummary/JobCandidateSummary';

interface JobCandidateInfoUserData extends Partial<CandidateData> {
  step_status?: string;
  need_sent_decision?: unknown;
  responsible_recruiter?: {
    uuid: string;
    full_name: string;
    avatar?: string | null;
    active_role?: number;
    roles?: Array<{ pk: number; name: string }>;
  } | null;
  potential_opening?: { uuid: string; name: string; expected?: string } | null;
}

interface JobCandidateInfoProps {
  step?: JobWorkflowStep | null;
  userData: JobCandidateInfoUserData;
  author?: { uuid?: string } | null;
  approversCandidate: JobApprover[];
  offer?: boolean;
  steps?: JobWorkflowStep[];
  setShowPopupDecision: (v: boolean) => void;
  setShowPopupWithdraw: (v: boolean) => void;
  openAddApprovers: () => void;
  setShowPopupFinalDecision: (v: boolean) => void;
  setShowPopupAssignToStep: (v: boolean) => void;
  setSubmitDecisionOffer: (v: boolean) => void;
  summaryEditing: boolean;
  onSummaryEditingChange: (editing: boolean) => void;
}

export const JobCandidateInfo: React.FC<JobCandidateInfoProps> = ({
  step,
  userData,
  author,
  approversCandidate,
  offer,
  ...props
}) => {
  const { t } = useTranslation();
  const {
    setShowPopupDecision,
    setShowPopupWithdraw,
    openAddApprovers,
    setShowPopupFinalDecision,
    setShowPopupAssignToStep,
    setSubmitDecisionOffer,
    summaryEditing,
    onSummaryEditingChange,
  } = props;
  const rejected = userData.step_status === 'Rejected';
  const ability = useAbility();
  const dispatch = useAppDispatch();
  const { id, candidate } = useParams<{ id: string; candidate: string }>();
  const user = useSelector(getUser);
  const activeStepIndex = step?.ordering_number;
  const canReassignResponsible = typeof activeStepIndex === 'number' && !!props.steps?.[activeStepIndex];
  const showAssignToMe =
    ability.can(ACTIONS.READ, UI.ASSIGN_CANDIDATE_TO_ME) && author?.uuid !== userData.responsible_recruiter?.uuid;
  const access_withdraw = ability.can(ACTIONS.READ, UI.ACTIONS_STEPS_WITHDRAW);
  const access_add_approver = ability.can(ACTIONS.READ, UI.ACTIONS_STEPS_ADD_APPROVER);
  const access_assign_to_step = ability.can(ACTIONS.READ, UI.ACTIONS_STEPS_ASSIGN_TO_STEP);
  const access_submit_final_decision = ability.can(
    ACTIONS.READ,
    subject(UI.SUBMIT_FINAL_DECISION, { step: step?.ordering_number })
  );
  const isApprover = approversCandidate.some(approver => approver.user.uuid === user?.uuid);
  const isResponsibleRecruiter = !!user?.uuid && userData.responsible_recruiter?.uuid === user.uuid;
  const hasAccess = access_withdraw || access_add_approver || access_assign_to_step || access_submit_final_decision;

  const location = `${userData.city ? `${userData.city}, ` : ''}${
    userData.state && userData.state !== 'Default state' ? `${userData.state}, ` : ''
  }${userData.country || ''}`;

  const fileList =
    userData.cvs?.map(cv => {
      const doc = cv as CandidateDocument;
      return { uid: doc.uuid, name: doc.filename };
    }) || [];

  const showExtraActions = !!step && Object.keys(step).length !== 0 && hasAccess;
  const [showReassignPopup, setShowReassignPopup] = useState(false);
  const [loadingReassignPopup, setLoadingShowReassignPopup] = useState(false);
  const [showAssignToMePopup, setShowAssignToMePopup] = useState(false);
  const [contactInfoExpanded, setContactInfoExpanded] = useState(false);
  const [openAttachPopup, setOpenAttachPopup] = useState(false);

  const onReassign = () => setShowReassignPopup(true);
  const onCloseReassignPopup = () => setShowReassignPopup(false);
  const onReassignRecruiter = (recruiter: string) => {
    if (!canReassignResponsible || !props.steps || activeStepIndex === undefined) return;
    setLoadingShowReassignPopup(true);
    dispatch(reassignResponsibleRecruiter({ candidate, job: id, recruiter }, props.steps, true, false, activeStepIndex))
      .then(() => {
        dispatch(getCandidateStepAssignment(id, candidate, false, props.steps, activeStepIndex));
      })
      .finally(() => {
        setLoadingShowReassignPopup(false);
        onCloseReassignPopup();
      });
  };

  const onAssignToMe = () => {
    setShowAssignToMePopup(false);
    onReassignRecruiter(user.uuid);
  };
  const onOpenAssignToMe = () => setShowAssignToMePopup(true);
  const onCloseAssignToMe = () => setShowAssignToMePopup(false);

  const onExpandContactInfo = () => {
    setContactInfoExpanded(e => !e);
  };

  const onSubmitDecision = () => {
    if (offer) setSubmitDecisionOffer(true);
    else setShowPopupDecision(true);
  };

  const openAttachFilePopup = () => {
    setOpenAttachPopup(true);
  };

  const closeAttachFilePopup = () => {
    setOpenAttachPopup(false);
  };

  const onAttachFiles = async ({ files }: { files: UploadFile[] }) => {
    const uploadedCvs = await uploadDocs(files.filter(cv => cv.originFileObj));
    if (uploadedCvs === null) return;
    const cvs = [...files.filter(cv => !cv.originFileObj).map(cv => cv.uid), ...uploadedCvs];
    dispatch(attachFilesToCandidate({ cvs }, id, candidate)).then(() => {
      notification({
        message: 'Document has been attached.',
        type: NOTIFICATION_TYPES.SUCCESS,
      });
      closeAttachFilePopup();
    });
  };

  return (
    <div className={cn('job-candidate-profile', { 'summary-editing': summaryEditing })}>
      <div className={cn('job-candidate-profile__header', { actions: showExtraActions })}>
        <div className="candidate-info">
          <div className="candidate-name">
            <div className="candidate-name__align">
              <Link
                to={`/candidates/${userData.uuid}/show`}
                className="candidate-name__latin"
                target="_blank"
                rel="noopener noreferrer"
              >
                {`${userData.first_name_latin} ${userData.last_name_latin}`}
              </Link>
              <Status type={getStatusType(userData.step_status)}>{userData.step_status}</Status>
            </div>
            <Typography.Title4 className="candidate-name__local" weight={TYPOGRAPHY_WEIGHT.BOLD}>
              {`${userData.first_name_local} ${userData.last_name_local}`}
            </Typography.Title4>
          </div>
        </div>
        {userData.need_sent_decision && (
          <Button type={BUTTON_TYPES.PRIMARY} onClick={onSubmitDecision} className="submit-decision">
            {t('translation:job_candidate_info.button_submit_decision')}
          </Button>
        )}
        {showExtraActions && (
          <AtsButtonMenu className="extra-actions">
            {access_add_approver && !offer && step.ordering_number < 5 && !rejected && (
              <li role="none" onClick={openAddApprovers}>
                {t('translation:job_candidate_info.menu_add_approver')}
              </li>
            )}
            {access_assign_to_step && step.ordering_number !== 6 && !rejected && (
              <li
                role="none"
                onClick={() => {
                  setShowPopupAssignToStep(true);
                }}
              >
                {t('translation:job_candidate_info.menu_assign_to_step')}
              </li>
            )}
            {access_submit_final_decision && ![5, 6].includes(step.ordering_number) && !rejected && (
              <li
                role="none"
                onClick={() => {
                  setShowPopupFinalDecision(true);
                }}
              >
                {t('translation:job_candidate_info.menu_final_decision')}
              </li>
            )}
            {access_withdraw && (
              <li
                role="none"
                onClick={() => {
                  setShowPopupWithdraw(true);
                }}
              >
                {t('translation:job_candidate_info.menu_withdraw')}
              </li>
            )}
          </AtsButtonMenu>
        )}
      </div>
      <Can I={ACTIONS.READ} a={subject(UI.AI_CANDIDATE_SUMMARY_VIEW, { isApprover }) as never}>
        <JobCandidateSummary
          editing={summaryEditing}
          stepName={step?.name}
          isApprover={isApprover}
          isResponsibleRecruiter={isResponsibleRecruiter}
          onEditingChange={onSummaryEditingChange}
        />
      </Can>
      <div className="job-candidate-profile__body">
        <Row gutter={32}>
          <Col lg={12} md={12} sm={24}>
            <div className="info-item">
              <Typography.Label>
                {t('translation:job_candidate_info.label_documents')}
                <Can I={ACTIONS.READ} a={UI.EDIT_CANDIDATE_FILES}>
                  <Button className="attach-file" type={BUTTON_TYPES.TEXT} onClick={openAttachFilePopup}>
                    <IconAttach />
                  </Button>
                </Can>
              </Typography.Label>
              {!userData.cvs?.length && <Typography.Text>-</Typography.Text>}
              {userData.cvs?.map(cv => {
                const file = candidateDocumentToFileItem(cv);
                return file && <File key={file.file} file={file} />;
              })}
            </div>
            <div className="info-item">
              <Typography.Label>{t('translation:job_candidate_info.label_cv_links')}</Typography.Label>
              {!userData.cvs_links?.length && <Typography.Text>-</Typography.Text>}
              {userData.cvs_links?.map(link => (
                <a key={link} className="cv-link" href={link} target="_blank" rel="noopener noreferrer">
                  <ChainLink className="cv-link__icon" />
                  <Typography.Text>{link}</Typography.Text>
                </a>
              ))}
            </div>
            <div className="info-item">
              <Typography.Label>{t('translation:job_candidate_info.label_location')}</Typography.Label>
              {!userData.country && !userData.timezone && <Typography.Text>-</Typography.Text>}
              {userData.country && (
                <div className="relocation-item">
                  <Location />
                  {location}
                </div>
              )}
              {userData.timezone && (
                <div className="relocation-item">
                  <Clock />
                  {userData.timezone}
                </div>
              )}
            </div>
          </Col>
          <Col lg={12} md={12} sm={24}>
            <div className="info-item">
              <Typography.Label>{t('translation:job_candidate_info.label_sources')}</Typography.Label>
              <Typography.Text>
                {userData.sources?.map(s => (
                  <div key={s.source}>
                    {s.additional_source_name ? (
                      <>
                        <span style={{ color: '#717C8C' }}>
                          {String(s.source).charAt(0).toUpperCase() + String(s.source).slice(1)}:{' '}
                        </span>
                        {s.additional_source_name}
                      </>
                    ) : (
                      s.source
                    )}
                    {userData.sources.length > 1 && s.is_main && (
                      <strong> {t('translation:job_candidate_info.text_main')}</strong>
                    )}
                  </div>
                ))}
              </Typography.Text>
            </div>
            <div className="info-item">
              <Typography.Label>{t('translation:job_candidate_info.label_potential_opening')}</Typography.Label>
              {userData.potential_opening ? (
                <>
                  <Link to={`/jobs/${id}/show/openings?scroll=${userData.potential_opening.uuid}`} target="_blank">
                    {userData.potential_opening.name}
                  </Link>
                  <Typography.Caption>
                    {t('translation:job_candidate_info.text_expected_on', {
                      date: dateFormatter(userData.potential_opening.expected),
                    } as Record<string, unknown>)}
                  </Typography.Caption>
                </>
              ) : (
                <Typography.Text>-</Typography.Text>
              )}
            </div>
            <div className="info-item">
              <Typography.Label>{t('translation:job_candidate_info.label_experience_level')}</Typography.Label>
              <Typography.Text>{userData.specialities?.[0]?.level || '-'}</Typography.Text>
            </div>
            <div className="info-item foreign-languages">
              <Typography.Label>{t('translation:job_candidate_info.label_languages')}</Typography.Label>
              {!userData.foreign_languages?.length && <Typography.Text>-</Typography.Text>}
              {userData.foreign_languages?.map(el => (
                <Typography.Text key={el.language + el.level}>{`${el.language}: ${el.level}`}</Typography.Text>
              ))}
            </div>
          </Col>
        </Row>
      </div>
      <Divider />
      <div
        className={cn('title contact-info', { open: contactInfoExpanded })}
        onClick={onExpandContactInfo}
        role="none"
      >
        <Typography.Title2>{t('translation:job_candidate_info.label_contact_information')}</Typography.Title2>
        <Vector className="vector" />
      </div>
      {contactInfoExpanded && (
        <Row gutter={32} className="job-candidate-profile__body">
          <Col lg={12} md={12} sm={24}>
            <div className="info-item">
              <Typography.Label>{t('translation:job_candidate_info.label_phone_number')}</Typography.Label>
              {userData.phone.map(p => (
                <Typography.Text>{p}</Typography.Text>
              ))}
              {!userData.phone[0] && <Typography.Text>-</Typography.Text>}
            </div>
            <div className="info-item">
              <Typography.Label>{t('translation:job_candidate_info.label_email_address')}</Typography.Label>
              {userData.email.map(e => (
                <Typography.Text>{e}</Typography.Text>
              ))}
              {!userData.email[0] && <Typography.Text>-</Typography.Text>}
            </div>
          </Col>
          <Col lg={12} md={12} sm={24}>
            <div className="info-item">
              <Typography.Label>{t('translation:job_candidate_info.label_telegram')}</Typography.Label>
              {userData.telegram.map(tg => (
                <Typography.Text>{tg}</Typography.Text>
              ))}
              {!userData.telegram[0] && <Typography.Text>-</Typography.Text>}
            </div>
          </Col>
          <Col span={24}>
            <div className="info-item network-links">
              <Typography.Label>{t('translation:job_candidate_info.label_links')}</Typography.Label>
              <Row gutter={32}>
                <Col lg={12} md={12} sm={24}>
                  {userData.social_networks.map(
                    (n, idx) =>
                      !(idx % 2) && (
                        <a href={n.link} target="_blank" rel="nofollow noreferrer">
                          <span>{n.link}</span> <span className="network">({n.network})</span>
                        </a>
                      )
                  )}
                  {!userData.social_networks.length && <Typography.Text>-</Typography.Text>}
                </Col>
                <Col lg={12} md={12} sm={24}>
                  {userData.social_networks.map(
                    (n, idx) =>
                      !!(idx % 2) && (
                        <a href={n.link} target="_blank" rel="nofollow noreferrer">
                          <span>{n.link}</span> <span className="network">({n.network})</span>
                        </a>
                      )
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      )}
      <Divider />
      <div className="title">
        <Typography.Title2>{t('translation:job_candidate_info.label_responsible_recruiter')}</Typography.Title2>
      </div>
      <User
        avatar={userData.responsible_recruiter?.avatar}
        fullName={userData.responsible_recruiter?.full_name}
        roles={[
          {
            name: userData.responsible_recruiter?.roles.find(
              ({ pk }) => pk === userData.responsible_recruiter.active_role
            )?.name,
          },
        ]}
      />
      <Can I={ACTIONS.READ} a={UI.EDIT_RESPONSIBLE_RECRUITER}>
        {access =>
          access && (
            <Button className="job-candidate-profile__reassign" type={BUTTON_TYPES.GHOST} onClick={onReassign}>
              {t('translation:job_candidate_info.button_reassign')}
            </Button>
          )
        }
      </Can>
      <Can I={ACTIONS.READ} a={UI.ASSIGN_CANDIDATE_TO_ME}>
        {showAssignToMe && (
          <Button className="job-candidate-profile__reassign" type={BUTTON_TYPES.GHOST} onClick={onOpenAssignToMe}>
            {t('translation:job_candidate_info.button_assign_to_me')}
          </Button>
        )}
      </Can>
      <Divider />
      <PopupReassignResponsibleRecruiter
        open={showReassignPopup}
        loading={loadingReassignPopup}
        onClose={onCloseReassignPopup}
        onOk={onReassignRecruiter}
        currentRecruiter={userData.responsible_recruiter}
      />
      <PopupAssignToMe
        open={showAssignToMePopup}
        onClose={onCloseAssignToMe}
        onOk={onAssignToMe}
        candidateName={`${userData.first_name_latin} ${userData.last_name_latin}`}
      />
      <PopupAttachFile open={openAttachPopup} list={fileList} onClose={closeAttachFilePopup} onAttach={onAttachFiles} />
    </div>
  );
};
